import React, { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Clock, Tag, User, Calendar, ChevronRight, ArrowRight, Share2 } from 'lucide-react';
import Seo from '@/components/shared/Seo';
import Breadcrumbs from '@/components/shared/Breadcrumbs';
import { findArticle, articles } from '@/data/blog/articles';
import { combineSchemas, organizationSchema, websiteSchema, articleSchema, breadcrumbSchema, faqSchema } from '@/lib/schemaOrg';

const Block = ({ b }) => {
  if (b.type === 'p') return <p className="text-gray-800 leading-relaxed mt-3">{b.text}</p>;
  if (b.type === 'h2') return <h2 id={b.id} className="mt-8 text-2xl font-bold text-black scroll-mt-24">{b.text}</h2>;
  if (b.type === 'h3') return <h3 className="mt-6 text-lg font-bold text-black">{b.text}</h3>;
  if (b.type === 'ul') return <ul className="mt-3 space-y-1 list-disc list-inside text-gray-800">{b.items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
  if (b.type === 'ol') return <ol className="mt-3 space-y-1 list-decimal list-inside text-gray-800">{b.items.map((it, i) => <li key={i}>{it}</li>)}</ol>;
  if (b.type === 'faq') return (
    <div className="mt-4 divide-y divide-gray-200 border border-gray-200 rounded-2xl bg-white">
      {b.items.map((f, i) => (
        <details key={i} className="p-4 group">
          <summary className="cursor-pointer text-sm font-bold text-black flex items-center justify-between gap-2">
            {f.q}
            <ChevronRight className="h-4 w-4 text-gray-400 group-open:rotate-90 transition" />
          </summary>
          <p className="mt-2 text-sm text-gray-700 leading-relaxed">{f.a}</p>
        </details>
      ))}
    </div>
  );
  if (b.type === 'cta') return (
    <div className="mt-6 bg-black text-white rounded-2xl p-5">
      <p className="text-sm text-white/80">{b.text}</p>
      <Link to={b.href} className="mt-3 inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-[#D3D86F] hover:bg-[#c4ca5e] text-black text-xs font-black uppercase tracking-wider">
        {b.label} <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
  return null;
};

const BlogArticlePage = () => {
  const { slug } = useParams();
  const article = findArticle(slug);

  const toc = useMemo(() => (article?.body || [])
    .filter((b) => b.type === 'h2' && b.id)
    .map((b) => ({ id: b.id, text: b.text })), [article]);

  const related = useMemo(() => {
    if (!article) return [];
    const same = articles.filter((a) => a.slug !== article.slug && a.category === article.category);
    if (same.length >= 3) return same.slice(0, 3);
    // Fill from same hub, then any recent, so the rail is never empty.
    const seen = new Set(same.map((a) => a.slug));
    const sameHub = articles.filter((a) => a.slug !== article.slug && a.hubSlug === article.hubSlug && !seen.has(a.slug));
    const combined = [...same, ...sameHub];
    combined.forEach((a) => seen.add(a.slug));
    if (combined.length >= 3) return combined.slice(0, 3);
    const others = articles.filter((a) => a.slug !== article.slug && !seen.has(a.slug));
    return [...combined, ...others].slice(0, 3);
  }, [article]);

  if (!article) return <Navigate to="/blog" replace />;

  const path = `/blog/${article.slug}`;
  const bcItems = [
    { name: 'Knowledge Hub', path: '/blog' },
    { name: article.title, path },
  ];

  const schemas = combineSchemas(
    organizationSchema(),
    websiteSchema(),
    articleSchema(article),
    breadcrumbSchema(bcItems),
    (article.faqsForSchema && article.faqsForSchema.length) ? faqSchema(article.faqsForSchema) : null,
  );

  const share = () => {
    const url = `https://winhousetutors.academy${path}`;
    if (navigator.share) {
      navigator.share({ title: article.title, text: article.description, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid={`blog-article-${article.slug}`}>
      <Seo
        title={`${article.title} | Winhouse Tutors`}
        description={article.description}
        path={path}
        jsonLd={schemas}
        articleMeta={{
          publishedAt: article.publishedAt,
          updatedAt: article.updatedAt || article.publishedAt,
          author: article.author?.name,
          section: article.category,
        }}
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Breadcrumbs items={bcItems} />

        <header className="mt-6 mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-red-700 font-bold flex items-center gap-2">
            <Tag className="h-3 w-3" /> {article.category}
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black text-black leading-tight" data-testid="article-h1">
            {article.title}
          </h1>
          <p className="mt-3 text-lg text-gray-700 leading-relaxed">{article.description}</p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {article.author?.name}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(article.publishedAt).toLocaleDateString()}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {article.readingMinutes} min read</span>
            <button onClick={share} className="ml-auto inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-gray-200 text-black hover:border-gray-300"
              data-testid="article-share">
              <Share2 className="h-3 w-3" /> Share
            </button>
          </div>
        </header>

        {toc.length > 0 && (
          <aside className="mb-8 bg-white border border-gray-200 rounded-2xl p-4" data-testid="article-toc">
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-black mb-2">In this article</div>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-800">
              {toc.map((h) => (
                <li key={h.id}><a href={`#${h.id}`} className="hover:text-red-700">{h.text}</a></li>
              ))}
            </ol>
          </aside>
        )}

        <article className="prose max-w-none" data-testid="article-body">
          {article.body.map((b, i) => <Block key={i} b={b} />)}
        </article>

        <section className="mt-12" data-testid="article-cta">
          <div className="bg-black text-white rounded-2xl p-6 sm:p-8">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D3D86F] font-bold">Take the next step</div>
            <h3 className="mt-2 text-2xl font-black">Turn what you just read into a plan</h3>
            <p className="mt-2 text-white/70 text-sm">Book a low-commitment trial lesson with a Winhouse tutor and get a personalised roadmap.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/get-started" className="h-11 px-6 rounded-full bg-[#D3D86F] hover:bg-[#c4ca5e] text-black text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5">Get Started</Link>
              <Link to={`/${article.hubSlug || 'online-tutoring'}`}
                className="h-11 px-6 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5"
                data-testid="article-hub-cta">
                Explore {article.category}
              </Link>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-10" data-testid="article-related">
            <h2 className="text-lg font-bold text-black">Related articles</h2>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {related.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`}
                  className="p-4 rounded-xl border border-gray-200 bg-white hover:border-red-300 transition"
                  data-testid={`article-related-${a.slug}`}>
                  <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">{a.category}</div>
                  <div className="text-sm font-bold text-black mt-1">{a.title}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default BlogArticlePage;
